function base64UrlEncode(bytes: Uint8Array) {
  let str = '';
  for (const byte of bytes) str += String.fromCharCode(byte);
  const base64 = btoa(str);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomString(length = 64) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += alphabet[bytes[i] % alphabet.length];
  return out;
}

/**
 * SHA-256 pure JavaScript fallback for non-secure contexts (HTTP).
 * crypto.subtle is only available in HTTPS or localhost.
 * This implementation follows FIPS 180-4.
 */
function sha256Js(data: Uint8Array): Uint8Array {
  const K: number[] = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  const ch = (x: number, y: number, z: number) => (x & y) ^ (~x & z);
  const maj = (x: number, y: number, z: number) => (x & y) ^ (x & z) ^ (y & z);
  const sig0 = (x: number) => rotr(x, 2) ^ rotr(x, 13) ^ rotr(x, 22);
  const sig1 = (x: number) => rotr(x, 6) ^ rotr(x, 11) ^ rotr(x, 25);
  const gam0 = (x: number) => rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3);
  const gam1 = (x: number) => rotr(x, 17) ^ rotr(x, 19) ^ (x >>> 10);

  // Pre-processing: padding
  const msgLen = data.length;
  const bitLen = msgLen * 8;
  // message + 1 byte (0x80) + padding + 8 bytes (length)
  const totalLen = msgLen + 1 + ((55 - msgLen % 64 + 64) % 64) + 8;
  const msg = new Uint8Array(totalLen);
  msg.set(data);
  msg[msgLen] = 0x80;
  // Append bit length as big-endian 64-bit (we only use 32 bits since JS)
  const view = new DataView(msg.buffer);
  view.setUint32(totalLen - 4, bitLen, false);

  // Initial hash values
  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const W = new Array<number>(64);

  for (let offset = 0; offset < totalLen; offset += 64) {
    for (let i = 0; i < 16; i++) {
      W[i] = view.getUint32(offset + i * 4, false);
    }
    for (let i = 16; i < 64; i++) {
      W[i] = (gam1(W[i - 2]) + W[i - 7] + gam0(W[i - 15]) + W[i - 16]) | 0;
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;

    for (let i = 0; i < 64; i++) {
      const t1 = (h + sig1(e) + ch(e, f, g) + K[i] + W[i]) | 0;
      const t2 = (sig0(a) + maj(a, b, c)) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0;
      d = c; c = b; b = a; a = (t1 + t2) | 0;
    }

    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }

  const result = new Uint8Array(32);
  const out = new DataView(result.buffer);
  out.setUint32(0, h0, false); out.setUint32(4, h1, false);
  out.setUint32(8, h2, false); out.setUint32(12, h3, false);
  out.setUint32(16, h4, false); out.setUint32(20, h5, false);
  out.setUint32(24, h6, false); out.setUint32(28, h7, false);
  return result;
}

async function sha256(value: string) {
  const data = new TextEncoder().encode(value);
  // crypto.subtle is only available in HTTPS or localhost contexts
  if (crypto.subtle) {
    const hash = await crypto.subtle.digest('SHA-256', data);
    return new Uint8Array(hash);
  }
  // Pure JS SHA-256 fallback for HTTP deployments
  return sha256Js(data);
}

export async function createPkcePair() {
  const verifier = randomString(96);
  // sha256() uses crypto.subtle when available, falls back to pure JS implementation
  // Both produce identical SHA-256 output → always use S256 method
  const challengeBytes = await sha256(verifier);
  const challenge = base64UrlEncode(challengeBytes);
  return { verifier, challenge, method: 'S256' as const };
}

export function buildKeycloakAuthorizeUrl(params: {
  baseUrl: string;
  realm: string;
  clientId: string;
  redirectUri: string;
  scope?: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod?: 'S256' | 'plain';
}) {
  const scope = params.scope ?? 'openid profile email';
  const url = new URL(`${params.baseUrl.replace(/\/+$/g, '')}/realms/${params.realm}/protocol/openid-connect/auth`);
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('redirect_uri', params.redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', scope);
  url.searchParams.set('state', params.state);
  url.searchParams.set('code_challenge', params.codeChallenge);
  url.searchParams.set('code_challenge_method', params.codeChallengeMethod ?? 'S256');
  return url.toString();
}

export function buildKeycloakTokenUrl(params: { baseUrl: string; realm: string }) {
  return `${params.baseUrl.replace(/\/+$/g, '')}/realms/${params.realm}/protocol/openid-connect/token`;
}

export function buildKeycloakLogoutUrl(params: {
  baseUrl: string;
  realm: string;
  postLogoutRedirectUri: string;
  idTokenHint?: string | null;
}) {
  const url = new URL(`${params.baseUrl.replace(/\/+$/g, '')}/realms/${params.realm}/protocol/openid-connect/logout`);
  url.searchParams.set('post_logout_redirect_uri', params.postLogoutRedirectUri);
  if (params.idTokenHint) url.searchParams.set('id_token_hint', params.idTokenHint);
  return url.toString();
}

export async function exchangeCodeForTokens(params: {
  tokenUrl: string;
  clientId: string;
  code: string;
  redirectUri: string;
  codeVerifier: string;
}) {
  const body = new URLSearchParams();
  body.set('grant_type', 'authorization_code');
  body.set('client_id', params.clientId);
  body.set('code', params.code);
  body.set('redirect_uri', params.redirectUri);
  body.set('code_verifier', params.codeVerifier);

  const response = await fetch(params.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Token exchange failed (${response.status})`);
  }

  return (await response.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in?: number;
    refresh_expires_in?: number;
    token_type?: string;
    session_state?: string;
    scope?: string;
  };
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function jwtIsExpired(token: string, skewSeconds = 30) {
  const payload = decodeJwtPayload(token);
  const exp = typeof payload?.exp === 'number' ? payload.exp : null;
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + skewSeconds;
}

